import { ErrorComponent, Page } from '@app-builder/components';
import { DetectionNavigationTabs } from '@app-builder/components/Detection';
import { CreateListModal } from '@app-builder/components/Lists/CreateListModal';
import { authMiddleware } from '@app-builder/middlewares/auth-middleware';
import { type CustomList } from '@app-builder/models/custom-list';
import { hasAnyEntitlement, isCreateListAvailable } from '@app-builder/services/feature-access';
import { fromUUIDtoSUUID } from '@app-builder/utils/short-uuid';
import * as Sentry from '@sentry/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { MarbleTableFeatures } from 'ui-design-system';
import { Table, Tag, useVirtualTable } from 'ui-design-system';

const listsLoader = createServerFn()
  .middleware([authMiddleware])
  .handler(async ({ context }) => {
    const { user, customListsRepository, entitlements } = context.authInfo;

    const customLists = await customListsRepository.listCustomLists();

    return {
      customLists,
      isCreateListAvailable: isCreateListAvailable(user),
      isIpGpsAvailable: hasAnyEntitlement(entitlements),
    };
  });

const columnHelper = createColumnHelper<MarbleTableFeatures, CustomList>();

export const Route = createFileRoute('/_app/_builder/detection/lists/')({
  loader: () => listsLoader(),
  errorComponent: ({ error }) => {
    Sentry.captureException(error);
    return <ErrorComponent error={error} />;
  },
  component: DetectionListsPage,
});

function DetectionListsPage() {
  const { t } = useTranslation(['lists', 'navigation']);
  const { customLists, isCreateListAvailable, isIpGpsAvailable } = Route.useLoaderData();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        id: 'name',
        header: t('lists:name'),
        size: 200,
        sortFn: 'text',
        enableSorting: true,
      }),
      columnHelper.accessor('description', {
        id: 'description',
        header: t('lists:description'),
        size: 400,
      }),
      columnHelper.accessor('kind', {
        id: 'kind',
        header: t('lists:kind'),
        size: 180,
        cell: ({ getValue }) => {
          const kind = getValue();
          return <Tag color="purple">{t(`lists:kind.${kind}`)}</Tag>;
        },
      }),
      columnHelper.accessor('ValuesCount', {
        id: 'valuesCount',
        header: t('lists:values_count'),
        size: 80,
        cell: ({ getValue }) => {
          const { count, hasMore } = getValue();
          return (
            <span>
              {count}
              {hasMore ? '+' : null} {t('lists:list.row.values_count', { count })}
            </span>
          );
        },
      }),
    ],
    [t],
  );

  const { table, isEmpty, getBodyProps, rows, getContainerProps } = useVirtualTable({
    data: customLists,
    columns,
    columnResizeMode: 'onChange',

    manualSorting: false,
    rowLink: ({ id }) => <Link to="/detection/lists/$listId" params={{ listId: fromUUIDtoSUUID(id) }} />,
  });

  return (
    <Page.Main>
      <Page.Content width="table">
        <DetectionNavigationTabs
          actions={isCreateListAvailable ? <CreateListModal isIpGpsAvailable={isIpGpsAvailable} /> : undefined}
        />
        <div className="flex flex-col gap-md">
          {isEmpty ? (
            <div className="bg-surface-card border-grey-border flex h-28 max-w-3xl flex-col items-center justify-center rounded-lg border border-solid p-md">
              <p className="text-s font-medium">{t('lists:empty_custom_lists_list')}</p>
            </div>
          ) : (
            <Table.Container {...getContainerProps()} className="bg-surface-card max-h-[70dvh]">
              <Table.Header headerGroups={table.getHeaderGroups()} />
              <Table.Body {...getBodyProps()}>
                {rows.map((row) => (
                  <Table.Row key={row.id} row={row} />
                ))}
              </Table.Body>
            </Table.Container>
          )}
        </div>
      </Page.Content>
    </Page.Main>
  );
}
